import * as XLSX from "xlsx";
import {
  Alert,
  AutoComplete,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  notification,
} from "antd";
import {
  BarChartOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  FileTextOutlined,
  InboxOutlined,
  LogoutOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import PharmacyNotificationBell from "../../components/PharmacyNotificationBell";
import {
  getMonthlyInvoices,
  getUsageSummary,
} from "../../services/usageBillingService";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  addInventoryItem,
  bulkAddInventory,
  createBillingRecord,
  dispensePrescription,
  getBillingRecords,
  getDispensedToday,
  getIncomingPrescriptions,
  getInventory,
  getMedicationNames,
  getPharmacyProfile,
  getPrescriptions,
  markAvailable,
  markOutOfStock,
  searchPrescriptions,
  updateInventoryItem,
} from "../../services/pharmacyService";
import { createTicket, getMyTickets } from "../../services/supportService";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);
const formatMonth = (m) => {
  if (!m) return "—";
  const [year, month] = m.split("-");
  return new Date(year, month - 1).toLocaleString("default", { month: "long", year: "numeric" });
};
const toCount = (d) => Array.isArray(d) ? d.length : (d?.count ?? d?.results?.length ?? 0); // eslint-disable-line no-unused-vars
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
const BILLING_COLOR = { DRAFT:"default", SUBMITTED:"blue", APPROVED:"green", REJECTED:"red", PAID:"cyan" };

// MENU_ITEMS is computed inside the component to show a live badge count on "incoming"

export default function PharmacyDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lowStockNotified = useRef(false);

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);

  const [prescriptions,        setPrescriptions]        = useState([]); // eslint-disable-line no-unused-vars
  const [inventory,            setInventory]            = useState([]);
  const [billingRows,          setBillingRows]          = useState([]); // eslint-disable-line no-unused-vars
  const [dispensedToday,       setDispensedToday]       = useState([]);
  const [pharmacyProfile,      setPharmacyProfile]      = useState(null);
  const [incomingPrescriptions,setIncomingPrescriptions]= useState([]);
  const [incomingCount,        setIncomingCount]        = useState(0);
  const [readyIds,             setReadyIds]             = useState(new Set());
  const [incomingLoading,      setIncomingLoading]      = useState(false);
  const [stats, setStats] = useState({ pendingRx:0, totalInventory:0, dispensedToday:0, billingRecords:0, incoming:0 });

  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchResults, setSearchResults] = useState(null);   // null = not searched yet
  const [searching,     setSearching]     = useState(false);

  const [addInvOpen,   setAddInvOpen]   = useState(false);
  const [stockOpen,    setStockOpen]    = useState(false);
  const [billingOpen,  setBillingOpen]  = useState(false);
  const [stockTarget,  setStockTarget]  = useState(null);
  const [dispensingId, setDispensingId] = useState(null);
  const [submitting,   setSubmitting]   = useState(false);

  const [addInvForm]  = Form.useForm();
  const [stockForm]   = Form.useForm();
  const [billingForm] = Form.useForm();

  // support
  const [myTickets,      setMyTickets]      = useState([]);
  const [ticketModal,    setTicketModal]    = useState(false);
  const [ticketDetail,   setTicketDetail]   = useState(null);
  const [supportLoaded,  setSupportLoaded]  = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [ticketForm]                        = Form.useForm();

  const [usageInvoices,  setUsageInvoices]  = useState([]);
  const [usageSummary,   setUsageSummary]   = useState(null);
  const [usageLoaded,    setUsageLoaded]    = useState(false);
  const [usageLoading,   setUsageLoading]   = useState(false);

  // Inventory search + bulk upload
  const [invSearch,       setInvSearch]       = useState("");
  const [bulkUploadOpen,  setBulkUploadOpen]  = useState(false);
  const [bulkPreview,     setBulkPreview]     = useState([]);
  const [bulkUploading,   setBulkUploading]   = useState(false);

  // Medication name autocomplete
  const [medNameOptions,  setMedNameOptions]  = useState([]);

  // Optimistic out-of-stock state for incoming prescriptions
  const [localOosRxIds,   setLocalOosRxIds]   = useState(new Set());


  const [invoiceDetailOpen,   setInvoiceDetailOpen]    = useState(false);
  const [invoiceDetailTarget, setInvoiceDetailTarget]  = useState(null);

  const todayISO = (() => { const d=new Date(); d.setHours(0,0,0,0); return d.toISOString(); })();

  const fetchPrescriptions = useCallback(async () => {
    const res = await getPrescriptions();
    const rows = toArray(res.data);
    setPrescriptions(rows.map(r=>({...r,key:r.id})));
    return rows;
  }, []);

  const fetchInventory = useCallback(async () => {
    const res = await getInventory();
    const rows = toArray(res.data);
    setInventory(rows.map(r=>({...r,key:r.id})));
    return rows;
  }, []);

  const fetchBilling = useCallback(async () => {
    const res = await getBillingRecords();
    const rows = toArray(res.data);
    setBillingRows(rows.map(r=>({...r,key:r.id})));
    return rows;
  }, []);

  const fetchDispensedToday = useCallback(async () => {
    try {
      const res = await getDispensedToday();
      const rows = toArray(res.data);
      setDispensedToday(rows.map(r=>({...r,key:r.id})));
      return rows;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [rxRes, invRes, billRes, profRes, dtRes, inRes] = await Promise.all([
          getPrescriptions(), getInventory(), getBillingRecords(),
          getPharmacyProfile(), getDispensedToday(),
          getIncomingPrescriptions().catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const rx      = toArray(rxRes.data);
        const inv     = toArray(invRes.data);
        const bill    = toArray(billRes.data);
        const dt      = toArray(dtRes.data);
        const incoming= toArray(inRes.data);
        const prof    = Array.isArray(profRes.data) ? profRes.data[0] : (profRes.data?.results?.[0] ?? profRes.data ?? null);
        setPrescriptions(rx.map(r=>({...r,key:r.id})));
        setInventory(inv.map(r=>({...r,key:r.id})));
        setBillingRows(bill.map(r=>({...r,key:r.id})));
        setDispensedToday(dt.map(r=>({...r,key:r.id})));
        setPharmacyProfile(prof);
        setIncomingPrescriptions(incoming.map(r=>({...r,key:r.id})));
        setIncomingCount(incoming.length);
        setStats({
          pendingRx:      rx.filter(r=>!r.is_dispensed&&r.is_active).length,
          totalInventory: inv.length,
          dispensedToday: dt.length,
          billingRecords: bill.length,
          incoming:       incoming.length,
        });
      } catch {
        if (!cancelled) setError("Failed to load dashboard data. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show a one-time low stock alert after inventory loads
  useEffect(() => {
    if (lowStockNotified.current || inventory.length === 0) return;
    const lowCount = inventory.filter(
      item => item.quantity_in_stock <= item.reorder_threshold
    ).length;
    if (lowCount > 0) {
      lowStockNotified.current = true;
      notification.warning({
        message: "Low Stock Alert",
        description: `${lowCount} medication${lowCount !== 1 ? "s" : ""} ${lowCount !== 1 ? "are" : "is"} below reorder threshold. Review your inventory.`,
        duration: 8,
        placement: "topRight",
      });
    }
  }, [inventory]);

  // Lazy-load usage billing data when navigating to that section
  useEffect(() => {
    if (selectedMenu !== "usage" || usageLoaded) return;
    let cancelled = false;
    async function loadUsage() {
      setUsageLoading(true);
      try {
        const [invRes, sumRes] = await Promise.all([
          getMonthlyInvoices(),
          getUsageSummary(),
        ]);
        if (cancelled) return;
        const invoices = invRes.data?.results ?? (Array.isArray(invRes.data) ? invRes.data : []);
        setUsageInvoices(invoices.map(r => ({ ...r, key: r.id })));
        const sumArr = Array.isArray(sumRes.data) ? sumRes.data : (sumRes.data ? [sumRes.data] : []);
        setUsageSummary(
          sumArr.find(s => s.provider_email === user.email || s.email === user.email)
            ?? sumArr[0] ?? null
        );
        setUsageLoaded(true);
      } catch {
        // silently fail — usage billing is non-critical
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    }
    loadUsage();
    return () => { cancelled = true; };
  }, [selectedMenu, usageLoaded]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── handlers ─────────────────────────────────────────────────────────────────
  const handleDispense = async (id) => {
    setDispensingId(id);
    try {
      await dispensePrescription(id);
      message.success("Prescription dispensed.");
      const rows = await fetchPrescriptions();
      setStats(s=>({
        ...s,
        pendingRx:      rows.filter(r=>!r.is_dispensed&&r.is_active).length,
        dispensedToday: rows.filter(r=>r.is_dispensed&&r.dispensed_at&&r.dispensed_at>=todayISO).length,
      }));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to dispense prescription.");
    } finally { setDispensingId(null); }
  };

  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) { message.warning("Enter a patient name or PIN to search."); return; }
    setSearching(true);
    try {
      // Try PIN first (all digits), otherwise search by name
      const params = /^\d+$/.test(q) ? { pin: q } : { name: q };
      const res = await searchPrescriptions(params);
      const rows = toArray(res.data);
      setSearchResults(rows.map(r=>({...r, key:r.id})));
      if (rows.length === 0) message.info("No pending prescriptions found for that patient.");
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Search failed. Please try again.");
    } finally { setSearching(false); }
  };

  const handleDispenseFromSearch = (record) => {
    const pickupSection = buildPickupConfirmSection(record);
    Modal.confirm({
      title:      "Mark as Dispensed? This cannot be undone.",
      icon:       null,
      width:      520,
      content: (
        <div>
          <p style={{marginBottom:6}}>
            <strong>Medication:</strong> {record.medication_name}{record.dosage ? ` — ${record.dosage}` : ""}
          </p>
          <p style={{marginBottom:6}}>
            <strong>Patient:</strong> {record.patient_name || "—"}
          </p>
          <p style={{marginBottom:12}}>
            <strong>PIN:</strong> <Text code style={{letterSpacing:2}}>{record.patient_pin || "—"}</Text>
          </p>
          {pickupSection}
          <Alert
            type="warning"
            showIcon
            message="Only confirm if this medication is being physically handed right now."
          />
        </div>
      ),
      okText:     "Confirm Dispense",
      okType:     "primary",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispensePrescription(record.id);
          message.success(`${record.medication_name} dispensed to ${record.patient_name || "patient"}.`);
          // Remove this row from search results and check if all are fulfilled
          setSearchResults(prev => {
            if (!prev) return prev;
            const remaining = prev.filter(r => r.id !== record.id);
            if (remaining.length === 0) {
              setTimeout(() => message.success("All prescriptions fulfilled for this patient.", 4), 100);
            }
            return remaining;
          });
          // Refresh dispensed-today list and stats
          await fetchDispensedToday();
          const rows = await fetchPrescriptions();
          setStats(s=>({
            ...s,
            pendingRx:      rows.filter(r=>!r.is_dispensed&&r.is_active).length,
            dispensedToday: rows.filter(r=>r.is_dispensed&&r.dispensed_at&&r.dispensed_at>=todayISO).length,
          }));
        } catch (err) {
          message.error(err?.response?.data?.detail ?? "Failed to dispense prescription.");
        }
      },
    });
  };

  const handleCannotFulfill = (record) => {
    Modal.confirm({
      title:      "Cannot Fulfill",
      content:    `Remove ${record.medication_name} from your queue? It will remain available at other pharmacies on the network.`,
      okText:     "Confirm",
      okType:     "default",
      cancelText: "Cancel",
      onOk: () => {
        setSearchResults(prev => prev ? prev.filter(r=>r.id !== record.id) : prev);
        message.info(`${record.medication_name} removed from queue. Patient may collect at another pharmacy.`);
      },
    });
  };

  const handleAddInventory = async (values) => {
    setSubmitting(true);
    try {
      await addInventoryItem({
        medication_name:   values.medication_name,
        quantity_in_stock: values.quantity_in_stock,
        reorder_threshold: values.reorder_threshold,
      });
      message.success("Inventory item added.");
      setAddInvOpen(false); addInvForm.resetFields();
      const rows = await fetchInventory();
      setStats(s=>({...s, totalInventory:rows.length}));
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.medication_name?.[0] ?? d?.quantity_in_stock?.[0] ?? d?.detail ?? "Failed to add item.");
    } finally { setSubmitting(false); }
  };

  const openStockModal = (record) => {
    setStockTarget(record);
    stockForm.setFieldsValue({ quantity_in_stock: record.quantity_in_stock });
    setStockOpen(true);
  };

  const handleUpdateStock = async (values) => {
    setSubmitting(true);
    try {
      await updateInventoryItem(stockTarget.id, { quantity_in_stock: values.quantity_in_stock });
      message.success("Stock updated.");
      setStockOpen(false); stockForm.resetFields();
      await fetchInventory();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.quantity_in_stock?.[0] ?? d?.detail ?? "Failed to update stock.");
    } finally { setSubmitting(false); }
  };

  const handleBilling = async (values) => {
    setSubmitting(true);
    try {
      await createBillingRecord({
        patient:       values.patient_id,
        service_date:  values.service_date,
        service_codes: ["Z99.9"],
        payor_type:    values.payor_type,
        amount_billed: parseFloat(values.amount_billed),
      });
      message.success("Billing record submitted.");
      setBillingOpen(false); billingForm.resetFields();
      const rows = await fetchBilling();
      setStats(s=>({...s, billingRecords:rows.length}));
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.patient?.[0] ?? d?.amount_billed?.[0] ?? d?.detail ?? "Failed to submit billing.");
    } finally { setSubmitting(false); }
  };

  // ── OOS handlers ─────────────────────────────────────────────────────────────
  const handleMarkOos = async (record) => {
    try {
      await markOutOfStock(record.id);
      setLocalOosRxIds(prev => new Set([...prev, record.id]));
      message.warning(`${record.medication_name} marked as out of stock.`);
      await loadIncoming();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to mark out of stock.");
    }
  };

  const handleMarkOosAvailable = async (record) => {
    try {
      await markAvailable(record.id);
      setLocalOosRxIds(prev => { const s = new Set(prev); s.delete(record.id); return s; });
      message.success(`${record.medication_name} marked as available again.`);
      await loadIncoming();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to mark available.");
    }
  };

  // ── Medication name autocomplete ──────────────────────────────────────────────
  const handleMedNameSearch = async (value) => {
    if (!value) { setMedNameOptions([]); return; }
    try {
      const res = await getMedicationNames(value);
      setMedNameOptions((res.data ?? []).map(n => ({ value: n })));
    } catch { /* silent */ }
  };

  // ── Bulk upload ───────────────────────────────────────────────────────────────
  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const parsed = rows
        .filter(r => r.medication_name || r["Medication Name"] || r["medication_name"])
        .map(r => ({
          medication_name:   String(r.medication_name ?? r["Medication Name"] ?? "").trim(),
          quantity_in_stock: parseInt(r.quantity_in_stock ?? r["Quantity in Stock"] ?? 0, 10) || 0,
          reorder_threshold: parseInt(r.reorder_threshold ?? r["Reorder Threshold"] ?? 0, 10) || 0,
        }))
        .filter(r => r.medication_name);
      setBulkPreview(parsed);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkUpload = async () => {
    if (!bulkPreview.length) return;
    setBulkUploading(true);
    try {
      const res = await bulkAddInventory(bulkPreview);
      const { created = 0, updated = 0 } = res.data ?? {};
      message.success(`Upload complete — ${created} added, ${updated} updated.`);
      setBulkUploadOpen(false);
      setBulkPreview([]);
      const rows = await fetchInventory();
      setStats(s => ({ ...s, totalInventory: rows.length }));
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.detail ?? "Bulk upload failed. Check the file format.");
    } finally { setBulkUploading(false); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [{ key:"logout", icon:<LogoutOutlined />, label:"Logout", danger:true, onClick:handleLogout }];

  // ── support ───────────────────────────────────────────────────────────────────
  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const res = await getMyTickets();
      setMyTickets(toArray(res.data).map(r=>({...r,key:r.id})));
      setSupportLoaded(true);
    } catch { /* silent */ } finally { setSupportLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedMenu === "support" && !supportLoaded) loadSupport();
  }, [selectedMenu, supportLoaded, loadSupport]);

  // ── incoming prescriptions: load + auto-refresh every 60 s ────────────────────
  const loadIncoming = useCallback(async () => {
    setIncomingLoading(true);
    try {
      const res = await getIncomingPrescriptions();
      const rows = toArray(res.data).map(r=>({...r,key:r.id}));
      setIncomingPrescriptions(rows);
      setIncomingCount(rows.length);
      setStats(s=>({...s, incoming:rows.length}));
    } catch { /* silent */ } finally { setIncomingLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedMenu !== "incoming") return;
    loadIncoming();
    const interval = setInterval(loadIncoming, 60000);
    return () => clearInterval(interval);
  }, [selectedMenu, loadIncoming]);

  const handleMarkReady = (recordId) => {
    setReadyIds(prev => new Set([...prev, recordId]));
    message.success("Prescription marked as ready for pickup.");
  };

  const handleDispenseIncoming = (record) => {
    const pickupSection = buildPickupConfirmSection(record);
    Modal.confirm({
      title:   "Mark as Dispensed? This cannot be undone.",
      icon:    null,
      width:   520,
      content:(
        <div>
          <p style={{marginBottom:6}}><strong>Medication:</strong> {record.medication_name}{record.dosage ? ` — ${record.dosage}` : ""}</p>
          <p style={{marginBottom:6}}><strong>Patient:</strong> {record.patient_name || "—"}</p>
          <p style={{marginBottom:12}}><strong>PIN:</strong> <Text code style={{letterSpacing:2}}>{record.patient_pin || "—"}</Text></p>
          {pickupSection}
          <Alert type="warning" showIcon message="Only confirm if this medication is being physically handed right now." />
        </div>
      ),
      okText:"Confirm Dispense", okType:"primary", cancelText:"Cancel",
      onOk: async () => {
        try {
          await dispensePrescription(record.id);
          message.success(`${record.medication_name} dispensed to ${record.patient_name || "patient"}.`);
          setIncomingPrescriptions(prev => prev.filter(r=>r.id!==record.id));
          setIncomingCount(prev => Math.max(0, prev - 1));
          setReadyIds(prev => { const s=new Set(prev); s.delete(record.id); return s; });
          setStats(s=>({...s, incoming:Math.max(0,s.incoming-1)}));
          await fetchDispensedToday();
          const rows = await fetchPrescriptions();
          setStats(s=>({...s, pendingRx:rows.filter(r=>!r.is_dispensed&&r.is_active).length}));
        } catch(err) {
          message.error(err?.response?.data?.detail ?? "Failed to dispense prescription.");
        }
      },
    });
  };

  const handleCreateTicket = async (values) => {
    try {
      await createTicket(values);
      message.success("Ticket submitted successfully.");
      setTicketModal(false);
      ticketForm.resetFields();
      loadSupport();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to submit ticket.");
    }
  };

  // ── helpers ───────────────────────────────────────────────────────────────────
  const buildPickupConfirmSection = (record) => {
    const auth = record.pickup_authorization_type;
    if (auth === "PARTNER_ORG" && record.pickup_partner_org_name) {
      return (
        <div style={{marginBottom:12,padding:"8px 12px",background:"#f0f5ff",border:"1px solid #adc6ff",borderRadius:6}}>
          <Text strong style={{color:"#1d39c4"}}>Authorized Partner: {record.pickup_partner_org_name}</Text>
          <br />
          <Text style={{fontSize:12}}>Partner ID: <Text code>{record.pickup_partner_org_code}</Text></Text>
          <br />
          <Text style={{fontSize:12,color:"#595959"}}>Verify the agent presents this partner ID and the patient PIN above.</Text>
        </div>
      );
    }
    if (auth === "PERSONAL_PROXY" && record.pickup_proxy_name) {
      return (
        <p style={{marginBottom:12}}>
          <strong>Authorized Representative:</strong> {record.pickup_proxy_name}
          {record.pickup_proxy_id ? <><br /><Text type="secondary" style={{fontSize:12}}>ID: {record.pickup_proxy_id}</Text></> : null}
        </p>
      );
    }
    return null;
  };

  // ── column defs ───────────────────────────────────────────────────────────────
  const prescriptionColumns = [ // eslint-disable-line no-unused-vars
    { title:"Patient Name",   dataIndex:"patient_name",      key:"patient_name",
      render:n=>n||<Text type="secondary">—</Text> },
    { title:"PIN",            dataIndex:"patient_pin",       key:"patient_pin",       width:100,
      render:pin=>pin?<Text code style={{letterSpacing:2}}>{pin}</Text>:<Text type="secondary">—</Text> },
    { title:"Medication",     dataIndex:"medication_name",   key:"medication_name" },
    { title:"Dosage",         dataIndex:"dosage",            key:"dosage",            width:100 },
    { title:"Frequency",      dataIndex:"frequency_display", key:"frequency_display", width:130,
      render:f=>f?<Tag>{f}</Tag>:<Text type="secondary">—</Text> },
    { title:"Prescribed By",  dataIndex:"doctor_email",      key:"doctor_email",
      render:e=>e||<Text type="secondary">—</Text> },
    { title:"Date",           dataIndex:"created_at",        key:"created_at",        width:110, render:fmtDate },
    { title:"Status",         key:"status",                  width:100,
      render:(_,r)=>r.is_dispensed
        ?<Tag color="green">Dispensed</Tag>
        :r.is_active
          ?<Tag color="orange">Pending</Tag>
          :<Tag color="red">Cancelled</Tag> },
    { title:"Actions",        key:"actions",                 width:100,
      render:(_,r)=>!r.is_dispensed&&r.is_active?(
        <Button size="small" type="primary" loading={dispensingId===r.id} onClick={()=>handleDispense(r.id)}>Dispense</Button>
      ):<Text type="secondary" style={{fontSize:12}}>—</Text> },
  ];

  // Columns for search results — pending prescriptions awaiting dispense
  const searchResultColumns = [
    { title:"Patient Name",  dataIndex:"patient_name",      key:"patient_name",
      render:n=>n||<Text type="secondary">—</Text> },
    { title:"PIN",           dataIndex:"patient_pin",       key:"patient_pin",       width:105,
      render:pin=>pin?<Text code style={{letterSpacing:2}}>{pin}</Text>:<Text type="secondary">—</Text> },
    { title:"Medication",    dataIndex:"medication_name",   key:"medication_name" },
    { title:"Dosage",        dataIndex:"dosage",            key:"dosage",            width:95 },
    { title:"Pickup",        key:"pickup",                  width:200,
      render:(_,r)=>{
        const auth = r.pickup_authorization_type;
        if (auth === "PARTNER_ORG" && r.pickup_partner_org_name)
          return <Tag color="purple" style={{fontWeight:600}}>{r.pickup_partner_org_name} ({r.pickup_partner_org_code})</Tag>;
        if (auth === "PERSONAL_PROXY" && r.pickup_proxy_name)
          return <Tag color="cyan">{r.pickup_proxy_name}</Tag>;
        return <Tag>Self</Tag>;
      }},
    { title:"Date Issued",   dataIndex:"created_at",        key:"created_at",        width:105, render:fmtDate },
    { title:"Actions",       key:"actions",                 width:240,
      render:(_,r)=>{
        const auth = r.pickup_authorization_type;
        let banner = null;
        if (auth === "PARTNER_ORG" && r.pickup_partner_org_name) {
          banner = (
            <Alert type="warning" showIcon style={{marginBottom:8,fontSize:12,padding:"4px 10px"}}
              message={<span>Verify agent presents <strong>{r.pickup_partner_org_name}</strong> ID (<code>{r.pickup_partner_org_code}</code>) and patient PIN before dispensing.</span>} />
          );
        } else if (auth === "PERSONAL_PROXY" && r.pickup_proxy_name) {
          banner = (
            <Alert type="info" showIcon style={{marginBottom:8,fontSize:12,padding:"4px 10px"}}
              message={<span>Authorized rep: <strong>{r.pickup_proxy_name}</strong>{r.pickup_proxy_id?` (ID: ${r.pickup_proxy_id})`:""} — verify ID document.</span>} />
          );
        } else {
          banner = (
            <Alert type="success" showIcon style={{marginBottom:8,fontSize:12,padding:"4px 10px"}}
              message="Patient will self-collect — verify their DHN PIN." />
          );
        }
        return (
          <div>
            {banner}
            <Space size={6}>
              <Button size="small" type="primary" onClick={()=>handleDispenseFromSearch(r)}>Mark Dispensed</Button>
              <Button size="small" danger onClick={()=>handleCannotFulfill(r)}>Cannot Fulfill</Button>
            </Space>
          </div>
        );
      }},
  ];

  // Columns for dispensed-today table
  const dispensedTodayColumns = [
    { title:"Patient Name", dataIndex:"patient_name",    key:"patient_name",
      render:n=>n||<Text type="secondary">—</Text> },
    { title:"PIN",          dataIndex:"patient_pin",     key:"patient_pin",     width:105,
      render:pin=>pin?<Text code style={{letterSpacing:2}}>{pin}</Text>:<Text type="secondary">—</Text> },
    { title:"Medication",   dataIndex:"medication_name", key:"medication_name" },
    { title:"Dosage",       dataIndex:"dosage",          key:"dosage",          width:95 },
    { title:"Dispensed At", dataIndex:"dispensed_at",    key:"dispensed_at",    width:160,
      render:iso=>iso?new Date(iso).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})+" — "+new Date(iso).toLocaleDateString():"—" },
  ];

  const inventoryColumns = [
    { title:"Medication Name",  dataIndex:"medication_name",  key:"medication_name" },
    { title:"Stock Level",      key:"stock",                  width:140,
      render:(_,r)=>{const low=r.quantity_in_stock<=r.reorder_threshold;
        return <Space size={4}>{low&&<Badge status="error" />}<Text style={{color:low?"#f5222d":undefined}}>{r.quantity_in_stock}</Text>{low&&<Tag color="red" style={{fontSize:11}}>Low</Tag>}</Space>;} },
    { title:"Reorder Threshold",dataIndex:"reorder_threshold",key:"reorder_threshold",width:150 },
    { title:"Last Updated",     dataIndex:"last_updated",     key:"last_updated",     width:120, render:fmtDate },
    { title:"Actions",          key:"actions",                width:120,
      render:(_,r)=><Button size="small" onClick={()=>openStockModal(r)}>Update Stock</Button> },
  ];

  const billingColumns = [ // eslint-disable-line no-unused-vars
    { title:"Patient",       dataIndex:"patient_email",         key:"patient_email",         render:e=>e??"—" },
    { title:"Provider",      dataIndex:"service_provider_email",key:"service_provider_email",render:e=>e??"—" },
    { title:"Service Date",  dataIndex:"service_date",          key:"service_date",           width:120, render:fmtDate },
    { title:"Amount Billed", dataIndex:"amount_billed",         key:"amount_billed",          width:130, render:a=>a!=null?`$${parseFloat(a).toFixed(2)}`:"—" },
    { title:"Payor",         dataIndex:"payor_type",            key:"payor_type",             width:100, render:t=>t?<Tag>{t}</Tag>:"—" },
    { title:"Status",        dataIndex:"submission_status",     key:"submission_status",      width:110,
      render:s=><Tag color={BILLING_COLOR[s]??"default"}>{s??"—"}</Tag> },
  ];

  const statCards = [
    { title:"Incoming Prescriptions", value:stats.incoming,        color: stats.incoming > 0 ? "#f5222d" : "#fa8c16",
      tooltip:"Prescriptions sent directly to your pharmacy — check the Incoming tab" },
    { title:"Pending Prescriptions", value:stats.pendingRx,       color:"#fa8c16",
      tooltip:"Search by patient PIN or name in Prescription Queue to dispense" },
    { title:"Dispensed Today",        value:stats.dispensedToday,  color:"#52c41a" },
    { title:"Inventory Items",        value:stats.totalInventory,  color:"#1677ff" },
  ];

  const menuItems = [
    { key:"dashboard", icon:<DashboardOutlined />, label:"Dashboard" },
    {
      key:"incoming",
      icon:<InboxOutlined />,
      label: incomingCount > 0 ? (
        <span style={{display:"flex",alignItems:"center",gap:8}}>
          Incoming
          <Badge count={incomingCount} style={{backgroundColor:"#f5222d"}} />
        </span>
      ) : "Incoming",
    },
    { key:"queue",     icon:<FileTextOutlined />,  label:"Prescription Queue" },
    { key:"inventory", icon:<DatabaseOutlined />,  label:"Inventory" },
    { key:"usage",     icon:<BarChartOutlined />,  label:"Usage & Invoices" },
    { key:"support",   icon:<CustomerServiceOutlined />, label:"Support" },
    { key:"profile",   icon:<UserOutlined />,           label:"My Profile" },
  ];

  // ── views ─────────────────────────────────────────────────────────────────────
  function renderContent() {
    switch (selectedMenu) {
      case "dashboard":
        return (
          <>
            <Row gutter={[16,16]} style={{marginBottom:24}}>
              {statCards.map(({title,value,color,tooltip})=>(
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{borderRadius:8}}>
                    <Statistic
                      title={
                        tooltip
                          ? <Tooltip title={tooltip}><Text style={{color:"#8c8c8c",fontSize:13,cursor:"help",textDecoration:"underline dotted"}}>{title}</Text></Tooltip>
                          : <Text style={{color:"#8c8c8c",fontSize:13}}>{title}</Text>
                      }
                      value={value} styles={{ content: {color,fontSize:28,fontWeight:600} }} />
                  </Card>
                </Col>
              ))}
            </Row>
            <Row gutter={[16,16]} style={{marginBottom:24}}>
              <Col xs={24}>
                <Card
                  variant="borderless"
                  style={{borderRadius:8}}
                  title={
                    <Space size={8}>
                      <Text strong>Dispensed Today</Text>
                      {dispensedToday.length > 0 && (
                        <Badge count={dispensedToday.length} style={{backgroundColor:"#52c41a"}} />
                      )}
                    </Space>
                  }>
                  <Table
                    columns={dispensedTodayColumns}
                    dataSource={dispensedToday.slice(0, 10)}
                    pagination={false}
                    locale={{emptyText:"No prescriptions dispensed today"}}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16,16]} style={{marginBottom:24}}>
              <Col xs={24}>
                <Card title={<Text strong>Medication Inventory</Text>} variant="borderless" style={{borderRadius:8}}
                  extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setAddInvOpen(true)}>Add Item</Button>}>
                  <Table columns={inventoryColumns} dataSource={inventory}
                    pagination={{pageSize:6,hideOnSinglePage:true}}
                    locale={{emptyText:"No inventory items found. Use 'Add Inventory Item' to add your first medication."}} size="small" />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16,16]}>
              <Col xs={24}>
                <Card title={<Text strong>Quick Actions</Text>} variant="borderless" style={{borderRadius:8}}>
                  <Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={()=>setAddInvOpen(true)}>Add Inventory Item</Button>
                    <Button icon={<DollarOutlined />} onClick={()=>setBillingOpen(true)}>Submit Billing</Button>
                  </Space>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "incoming":
        return (
          <Spin spinning={incomingLoading}>
            <Title level={4} style={{marginBottom:8,color:"#262626"}}>Incoming Prescriptions</Title>
            <Text type="secondary" style={{display:"block",marginBottom:16}}>
              Prescriptions assigned to your pharmacy and advance preparation requests.
            </Text>
            <Alert
              type="success"
              showIcon
              message="These patients are expecting their medication to be ready for pickup. Please prepare these in advance."
              style={{marginBottom:16,borderRadius:8}}
            />
            <Card variant="borderless" style={{borderRadius:8}}>
              <Table
                size="small"
                dataSource={incomingPrescriptions}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:(
                  <div style={{textAlign:"center",padding:"32px 0"}}>
                    <InboxOutlined style={{fontSize:40,color:"#d9d9d9",marginBottom:8,display:"block"}} />
                    <Text type="secondary">No incoming prescriptions. Directed prescriptions will appear here when doctors send them to your pharmacy.</Text>
                  </div>
                )}}
                rowClassName={(r) => readyIds.has(r.id) ? "incoming-ready-row" : ""}
                onRow={(r) => readyIds.has(r.id) ? {style:{backgroundColor:"#f6ffed"}} : {}}
                columns={[
                  { title:"Received",     dataIndex:"created_at",   key:"created_at",  width:100, render:fmtDate },
                  { title:"Patient",      dataIndex:"patient_name", key:"patient_name",
                    render:n=>n||<Text type="secondary">—</Text> },
                  { title:"PIN",          dataIndex:"patient_pin",  key:"patient_pin", width:120,
                    render:pin=>pin
                      ?<span style={{background:"#F3F4F6",borderRadius:4,padding:"2px 6px",fontFamily:"monospace",whiteSpace:"nowrap",fontSize:13,letterSpacing:1}}>{pin}</span>
                      :<Text type="secondary">—</Text> },
                  { title:"Medication",   key:"medication",
                    render:(_,r)=>(
                      <div>
                        <Text strong style={{display:"block",fontSize:13}}>{r.medication_name||"—"}{r.dosage?` ${r.dosage}`:""}</Text>
                        {(r.frequency_display||r.duration)&&(
                          <Text type="secondary" style={{fontSize:11}}>
                            {[r.frequency_display,r.duration].filter(Boolean).join(" • ")}
                          </Text>
                        )}
                      </div>
                    )},
                  { title:"Queue Type",   key:"queue_type",         width:130,
                    render:(_,r)=>{
                      const myId = pharmacyProfile?.id;
                      if (myId && r.pharmacy === myId) return <Tag color="blue">Assigned</Tag>;
                      return <Tag color="orange">Advance Notice</Tag>;
                    }},
                  { title:"Prescribed By",key:"doctor",             width:140,
                    render:(_,r)=><Text>{r.doctor_name??r.doctor_email??"—"}</Text> },
                  { title:"Pickup",       key:"pickup",             width:180,
                    render:(_,r)=>{
                      const auth = r.pickup_authorization_type;
                      if (auth === "PARTNER_ORG" && r.pickup_partner_org_name)
                        return <Tag color="purple" style={{fontWeight:600}}>{r.pickup_partner_org_name} ({r.pickup_partner_org_code})</Tag>;
                      if (auth === "PERSONAL_PROXY" && r.pickup_proxy_name)
                        return <Tag color="cyan">{r.pickup_proxy_name}</Tag>;
                      return <Tag>Self</Tag>;
                    }},
                  { title:"Status",       key:"status",             width:90,
                    render:(_,r)=>readyIds.has(r.id)
                      ?<Tag color="green">Ready</Tag>
                      :<Tag color="orange">Pending</Tag> },
                  { title:"Actions",      key:"actions",            width:260,
                    render:(_,r)=>{
                      const isOos = localOosRxIds.has(r.id) ||
                        (r.out_of_stock_pharmacy_ids ?? []).includes(pharmacyProfile?.id);
                      return (
                        <Space size={4} wrap>
                          {!readyIds.has(r.id) && !isOos && (
                            <Button size="small" style={{color:"#52c41a",borderColor:"#52c41a"}}
                              onClick={() => handleMarkReady(r.id)}>
                              Mark Ready
                            </Button>
                          )}
                          {isOos ? (
                            <>
                              <Tag color="orange" style={{marginRight:0}}>Out of Stock</Tag>
                              <Button size="small" onClick={() => handleMarkOosAvailable(r)}>
                                Mark Available
                              </Button>
                            </>
                          ) : (
                            <Button size="small"
                              style={{color:"#d46b08",borderColor:"#ffa940"}}
                              onClick={() => handleMarkOos(r)}>
                              Out of Stock
                            </Button>
                          )}
                          {!isOos && (
                            <Button size="small" type="primary"
                              onClick={() => handleDispenseIncoming(r)}>
                              Dispense
                            </Button>
                          )}
                        </Space>
                      );
                    }},
                ]}
              />
            </Card>
          </Spin>
        );

      case "queue":
        return (
          <>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>Prescription Queue</Title>

            {/* ── Search Bar ── */}
            <Card variant="borderless" style={{borderRadius:8, marginBottom:16}}>
              <Row gutter={12} align="middle">
                <Col flex="auto">
                  <Input
                    size="large"
                    placeholder="Enter patient name or PIN..."
                    prefix={<SearchOutlined style={{color:"#bfbfbf"}} />}
                    value={searchQuery}
                    onChange={e=>setSearchQuery(e.target.value)}
                    onPressEnter={handleSearch}
                    allowClear
                    onClear={()=>{ setSearchQuery(""); setSearchResults(null); }}
                  />
                </Col>
                <Col>
                  <Button type="primary" size="large" icon={<SearchOutlined />}
                    loading={searching} onClick={handleSearch}>
                    Search
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* ── Empty state (not yet searched) ── */}
            {searchResults === null && (
              <Card variant="borderless" style={{borderRadius:8, marginBottom:16, textAlign:"center", padding:"32px 0"}}>
                <SearchOutlined style={{fontSize:40, color:"#d9d9d9", marginBottom:12, display:"block"}} />
                <Text type="secondary" style={{fontSize:15}}>
                  Enter a patient name or PIN to find their prescriptions
                </Text>
              </Card>
            )}

            {/* ── Search Results ── */}
            {searchResults !== null && (
              <Card
                variant="borderless"
                style={{borderRadius:8, marginBottom:16}}
                title={
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Text strong>
                        Prescriptions for: <Text style={{color:"#1677ff"}}>"{searchQuery}"</Text>
                        {searchResults.length > 0 &&
                          <Tag color="orange" style={{marginLeft:8}}>{searchResults.length} pending</Tag>}
                      </Text>
                    </Col>
                    <Col>
                      <Button size="small" onClick={()=>{ setSearchQuery(""); setSearchResults(null); }}>
                        Clear
                      </Button>
                    </Col>
                  </Row>
                }>
                {searchResults.length === 0 ? (
                  <div style={{textAlign:"center", padding:"24px 0"}}>
                    <Text type="secondary">
                      No pending prescriptions found for "{searchQuery}". The patient may not have any active prescriptions, or they may have selected a different pharmacy.
                    </Text>
                  </div>
                ) : (
                  <Table
                    columns={searchResultColumns}
                    dataSource={searchResults}
                    pagination={{pageSize:10, hideOnSinglePage:true}}
                    size="small"
                    scroll={{x:900}}
                  />
                )}
              </Card>
            )}

            {/* ── Dispensed Today ── */}
            <Card
              variant="borderless"
              style={{borderRadius:8}}
              title={
                <Text strong>
                  Dispensed Today
                  {dispensedToday.length > 0 &&
                    <Tag color="green" style={{marginLeft:8}}>{dispensedToday.length}</Tag>}
                </Text>
              }>
              <Table
                columns={dispensedTodayColumns}
                dataSource={dispensedToday}
                pagination={{pageSize:10, hideOnSinglePage:true}}
                locale={{emptyText:"No prescriptions dispensed today."}}
                size="small"
              />
            </Card>
          </>
        );

      case "inventory": {
        const filteredInventory = invSearch
          ? inventory.filter(r => r.medication_name?.toLowerCase().includes(invSearch.toLowerCase()))
          : inventory;
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>Medication Inventory</Title>
            <Card variant="borderless" style={{borderRadius:8, marginBottom:16}}>
              <Row gutter={12} align="middle">
                <Col flex="auto">
                  <Input
                    placeholder="Search by medication name…"
                    prefix={<SearchOutlined style={{color:"#bfbfbf"}} />}
                    value={invSearch}
                    onChange={e => setInvSearch(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col>
                  <Space>
                    <Button icon={<UploadOutlined />} onClick={() => { setBulkPreview([]); setBulkUploadOpen(true); }}>
                      Upload Formulary
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddInvOpen(true)}>
                      Add Item
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>
            <Card variant="borderless" style={{borderRadius:8}}
              title={
                <Space size={8}>
                  <Text strong>All Inventory Items</Text>
                  {invSearch && <Tag color="blue">{filteredInventory.length} result{filteredInventory.length !== 1 ? "s" : ""}</Tag>}
                </Space>
              }>
              <Table columns={inventoryColumns} dataSource={filteredInventory}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:invSearch ? "No items match your search." : "No inventory items found. Use 'Add Inventory Item' to add your first medication."}} size="small" />
            </Card>
          </>
        );
      }

      case "usage": {
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const currentInvoice = usageInvoices.find(inv => inv.billing_month === currentMonth) ?? null;
        const INVOICE_STATUS_COLOR = { DRAFT:"default", SENT:"blue", PAID:"green", OVERDUE:"red" };
        const invoiceColumns = [
          { title:"Month",              dataIndex:"billing_month", key:"billing_month", width:150,
            render:m => formatMonth(m) },
          { title:"Rx Dispensed",       key:"disp",
            render:(_,r) => r.total_prescriptions_dispensed ?? 0 },
          { title:"Total Amount",       dataIndex:"total_amount",  key:"total_amount",  width:130,
            render:a => a!=null ? `$${parseFloat(a).toFixed(2)}` : "—" },
          { title:"Status",             dataIndex:"status",        key:"status",        width:100,
            render:s => <Tag color={INVOICE_STATUS_COLOR[s]??"default"}>{s??"—"}</Tag> },
          { title:"Actions",            key:"actions",             width:110,
            render:(_,r) => (
              <Button size="small"
                onClick={() => { setInvoiceDetailTarget(r); setInvoiceDetailOpen(true); }}>
                View Details
              </Button>
            )},
        ];
        return (
          <Spin spinning={usageLoading} description="Loading…">
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>Usage &amp; Invoices</Title>
            {/* Current Month Summary */}
            <Row gutter={[16,16]} style={{marginBottom:24}}>
              <Col xs={24} sm={12}>
                <Card variant="borderless" style={{borderRadius:8}}>
                  <Statistic
                    title={<Text style={{color:"#8c8c8c",fontSize:13}}>Prescriptions Dispensed (This Month)</Text>}
                    value={usageSummary?.prescriptions_dispensed ?? currentInvoice?.total_prescriptions_dispensed ?? 0}
                    styles={{ content: {color:"#1677ff",fontSize:28,fontWeight:600} }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card variant="borderless" style={{borderRadius:8}}>
                  <Statistic
                    title={<Text style={{color:"#8c8c8c",fontSize:13}}>Estimated Fee (This Month)</Text>}
                    value={usageSummary?.estimated_fee != null
                      ? parseFloat(usageSummary.estimated_fee).toFixed(2)
                      : (currentInvoice?.total_amount != null ? parseFloat(currentInvoice.total_amount).toFixed(2) : "0.00")}
                    prefix="$"
                    styles={{ content: {color:"#fa8c16",fontSize:28,fontWeight:600} }}
                  />
                </Card>
              </Col>
            </Row>
            {/* Invoice History */}
            <Card variant="borderless" style={{borderRadius:8}} title={<Text strong>Invoice History</Text>}>
              <Table
                columns={invoiceColumns}
                dataSource={usageInvoices}
                pagination={{pageSize:12,hideOnSinglePage:true}}
                locale={{emptyText:"No invoices generated yet"}}
                size="small"
              />
            </Card>
          </Spin>
        );
      }

      case "profile":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>My Profile</Title>
            <Card variant="borderless" style={{borderRadius:8}}>
              {pharmacyProfile ? (
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Email">{pharmacyProfile.user_email ?? user.email}</Descriptions.Item>
                  <Descriptions.Item label="Pharmacy Name">{pharmacyProfile.pharmacy_name ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="License No.">{pharmacyProfile.license_number ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{pharmacyProfile.phone ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Verified">
                    {pharmacyProfile.is_verified ? <Tag color="green">Verified</Tag> : <Tag color="orange">Pending</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>{pharmacyProfile.address ?? "—"}</Descriptions.Item>
                </Descriptions>
              ) : (
                <Text type="secondary">No profile found. Please contact your administrator.</Text>
              )}
            </Card>
            <ChangePasswordForm />
          </>
        );

      case "support":
        return (
          <Spin spinning={supportLoading}>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>Support</Title>
            <Card variant="borderless" style={{borderRadius:8}} title={<Text strong>My Tickets</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setTicketModal(true)}>Report an Issue</Button>}>
              <Table size="small" dataSource={myTickets} pagination={{pageSize:10,hideOnSinglePage:true}}
                locale={{emptyText:"No tickets yet"}}
                columns={[
                  { title:"ID",       dataIndex:"id",       key:"id",       width:90, render:id=><Text code style={{fontSize:11}}>{String(id).slice(0,8)}</Text> },
                  { title:"Subject",  dataIndex:"subject",  key:"subject",  ellipsis:true },
                  { title:"Priority", dataIndex:"priority", key:"priority", width:100, render:p=><Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[p]??"default"}>{p}</Tag> },
                  { title:"Status",   dataIndex:"status",   key:"status",   width:110, render:s=><Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default",ESCALATED:"red"}[s]??"default"}>{s?.replace(/_/g," ")}</Tag> },
                  { title:"Created",  dataIndex:"created_at",key:"created_at",width:110, render:fmtDate },
                  { title:"Actions",  key:"actions",        width:80, render:(_,r)=><Button size="small" onClick={()=>setTicketDetail(r)}>View</Button> },
                ]}
              />
            </Card>
          </Spin>
        );

      default: return null;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <Layout style={{minHeight:"100vh"}}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{background:"#fff",borderRight:"1px solid #f0f0f0"}}>
        <div style={{height:64,display:"flex",alignItems:"center",justifyContent:"center",
          borderBottom:"1px solid #f0f0f0",padding:"0 16px"}}>
          {!collapsed && (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",lineHeight:1.3}}>
              <Text strong style={{color:"#1677ff",fontSize:13,whiteSpace:"nowrap"}}>Digital Healthcare Network</Text>
              <Text style={{color:"#1677ff",fontSize:12,whiteSpace:"nowrap"}}>Pharmacy</Text>
            </div>
          )}
        </div>
        <Menu mode="inline" selectedKeys={[selectedMenu]} items={menuItems}
          onClick={({key})=>setSelectedMenu(key)} style={{border:"none",marginTop:8}} />
      </Sider>

      <Layout>
        <Header style={{background:"#fff",padding:"0 24px",display:"flex",alignItems:"center",
          justifyContent:"space-between",borderBottom:"1px solid #f0f0f0",position:"sticky",top:0,zIndex:100}}>
          <div>
            <Text strong style={{fontSize:16,color:"#1677ff"}}>MCHUSA</Text>
            <Text style={{fontSize:14,color:"#8c8c8c",marginLeft:8}}>| Digital Healthcare Network</Text>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <PharmacyNotificationBell onNavigate={(section) => setSelectedMenu(section)} />
            <Dropdown menu={{items:userMenuItems}} placement="bottomRight" arrow>
              <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <Avatar size={32} icon={<UserOutlined />} style={{background:"#13c2c2"}} />
                <Text style={{fontSize:14,color:"#262626"}}>{user.email??"Pharmacist"}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{padding:24,background:"#f5f7fa"}}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3, 4].map(i => (
                  <Col xs={24} sm={12} lg={6} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 24 }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Card>
              <Card variant="borderless" style={{ borderRadius: 8 }}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </Card>
            </>
          ) : error ? (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)} closable />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── New Ticket Modal ── */}
      <Modal title="Report an Issue" open={ticketModal}
        onOk={()=>ticketForm.submit()} onCancel={()=>{ setTicketModal(false); ticketForm.resetFields(); }}
        okText="Submit" destroyOnHidden keyboard>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}>
          <Form.Item name="subject" label="Subject" rules={[{required:true,message:"Required"}]}>
            <Input placeholder="Brief description of the issue" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{required:true,message:"Select priority"}]}>
            <Select placeholder="Select priority">
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="CRITICAL">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{required:true,message:"Required"}]}>
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={()=>setTicketDetail(null)}
        footer={<Button onClick={()=>setTicketDetail(null)}>Close</Button>}
        destroyOnHidden keyboard width={500}>
        {ticketDetail && (
          <div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <tbody>
                {[
                  ["Subject",  ticketDetail.subject],
                  ["Priority", <Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",   <Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default",ESCALATED:"red"}[ticketDetail.status]??"default"}>{ticketDetail.status?.replace(/_/g," ")}</Tag>],
                  ["Created",  new Date(ticketDetail.created_at).toLocaleString()],
                  ["Updated",  new Date(ticketDetail.updated_at).toLocaleString()],
                ].map(([label,val])=>(
                  <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                    <td style={{padding:"8px 12px",color:"#8c8c8c",width:"35%"}}>{label}</td>
                    <td style={{padding:"8px 12px"}}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:12}}>
              <Text strong>Description</Text>
              <div style={{marginTop:6,padding:10,background:"#fafafa",borderRadius:4,fontSize:13,color:"#595959"}}>{ticketDetail.description}</div>
            </div>
            {ticketDetail.status === "ESCALATED" && (
              <div style={{marginTop:12,padding:10,background:"#fff1f0",border:"1px solid #ffa39e",borderRadius:4}}>
                <Text strong style={{color:"#cf1322"}}>Escalated to MCHUSA</Text>
                <div style={{marginTop:4,fontSize:13,color:"#595959"}}>
                  Your ticket has been escalated to MCHUSA for direct resolution. You will be notified once it is resolved.
                </div>
              </div>
            )}
            {ticketDetail.resolution_notes && (
              <div style={{marginTop:12}}>
                <Text strong>Resolution Notes</Text>
                <div style={{marginTop:6,padding:10,background:"#f6ffed",border:"1px solid #b7eb8f",borderRadius:4,fontSize:13}}>{ticketDetail.resolution_notes}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Add Inventory Modal ── */}
      <Modal title="Add Inventory Item" open={addInvOpen} onOk={()=>addInvForm.submit()}
        onCancel={()=>{setAddInvOpen(false);addInvForm.resetFields();}}
        okText="Add" confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Form form={addInvForm} layout="vertical" onFinish={handleAddInventory}>
          <Form.Item name="medication_name" label="Medication Name" rules={[{required:true,message:"Required"}]}>
            <AutoComplete
              options={medNameOptions}
              onSearch={handleMedNameSearch}
              placeholder="e.g. Amoxicillin 500mg"
              filterOption={false}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="quantity_in_stock" label="Initial Quantity" rules={[{required:true,message:"Required"}]}>
                <InputNumber min={0} style={{width:"100%"}} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reorder_threshold" label="Reorder Threshold" rules={[{required:true,message:"Required"}]}>
                <InputNumber min={0} style={{width:"100%"}} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Update Stock Modal ── */}
      <Modal title={`Update Stock — ${stockTarget?.medication_name??""}`} open={stockOpen}
        onOk={()=>stockForm.submit()} onCancel={()=>{setStockOpen(false);stockForm.resetFields();}}
        okText="Update" confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Form form={stockForm} layout="vertical" onFinish={handleUpdateStock}>
          <Form.Item name="quantity_in_stock" label="New Quantity in Stock" rules={[{required:true,message:"Required"}]}>
            <InputNumber min={0} style={{width:"100%"}} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Submit Billing Modal ── */}
      <Modal title="Submit Billing Record" open={billingOpen} onOk={()=>billingForm.submit()}
        onCancel={()=>{setBillingOpen(false);billingForm.resetFields();}}
        okText="Submit" confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Form form={billingForm} layout="vertical" onFinish={handleBilling}>
          <Form.Item name="patient_id" label="Patient UUID" rules={[{required:true,message:"Required"}]}>
            <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
          </Form.Item>
          <Form.Item name="service_date" label="Service Date" rules={[{required:true,message:"Required"}]}>
            <Input type="date" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amount_billed" label="Amount Billed"
                rules={[{required:true,message:"Required"},{validator:(_,v)=>!v||parseFloat(v)>0?Promise.resolve():Promise.reject("Must be > 0")}]}>
                <Input type="number" min="0.01" step="0.01" prefix="$" placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payor_type" label="Payor Type" rules={[{required:true,message:"Required"}]}>
                <Select placeholder="Select">
                  <Option value="GOVERNMENT">State Government</Option>
                  <Option value="PRIVATE">Insurance</Option>
                  <Option value="SELF_PAY">Self-Pay</Option>
                  <Option value="HMO">HMO</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      {/* ── Bulk Formulary Upload Modal ── */}
      <Modal
        title="Upload Formulary"
        open={bulkUploadOpen}
        onCancel={() => { setBulkUploadOpen(false); setBulkPreview([]); }}
        footer={[
          <Button key="cancel" onClick={() => { setBulkUploadOpen(false); setBulkPreview([]); }}>Cancel</Button>,
          <Button key="upload" type="primary" icon={<UploadOutlined />}
            loading={bulkUploading}
            disabled={bulkPreview.length === 0}
            onClick={handleBulkUpload}>
            Confirm Upload ({bulkPreview.length} items)
          </Button>,
        ]}
        width={640}
        destroyOnHidden>
        <Alert
          type="info"
          showIcon
          style={{marginBottom:16}}
          message="Excel format: columns medication_name, quantity_in_stock, reorder_threshold. Existing items are updated; new items are added."
        />
        <div style={{marginBottom:16}}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleBulkFileChange}
            style={{display:"block",marginBottom:8}}
          />
        </div>
        {bulkPreview.length > 0 && (
          <Table
            size="small"
            dataSource={bulkPreview.map((r, i) => ({ ...r, key: i }))}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            columns={[
              { title: "Medication Name",   dataIndex: "medication_name",   key: "medication_name" },
              { title: "Quantity in Stock", dataIndex: "quantity_in_stock", key: "quantity_in_stock", width: 130 },
              { title: "Reorder Threshold", dataIndex: "reorder_threshold", key: "reorder_threshold", width: 140 },
            ]}
          />
        )}
        {bulkPreview.length === 0 && (
          <div style={{textAlign:"center",padding:"24px 0",color:"#8c8c8c",fontSize:13}}>
            Select an Excel file to preview before uploading
          </div>
        )}
      </Modal>

      {/* ── Invoice Detail Modal ── */}
      {(() => {
        const r         = invoiceDetailTarget;
        const dCount    = r?.total_prescriptions_dispensed ?? 0;
        const dSubtotal = parseFloat(r?.prescription_dispensed_fee ?? 0);
        const dFeeUnit  = dCount > 0 ? (dSubtotal / dCount) : 0;
        const INV_COLOR = { DRAFT:"default", SENT:"blue", PAID:"green", OVERDUE:"red" };
        return (
          <Modal
            title={`Invoice — ${formatMonth(r?.billing_month)}`}
            open={invoiceDetailOpen}
            onCancel={() => { setInvoiceDetailOpen(false); setInvoiceDetailTarget(null); }}
            footer={[
              <Button key="close" onClick={() => { setInvoiceDetailOpen(false); setInvoiceDetailTarget(null); }}>
                Close
              </Button>,
            ]}
            destroyOnHidden>
            <Descriptions column={1} bordered size="small" style={{marginBottom:16}}>
              <Descriptions.Item label="Billing Month">{formatMonth(r?.billing_month)}</Descriptions.Item>
              <Descriptions.Item label="Prescriptions Dispensed">
                {dCount} × ${dFeeUnit.toFixed(2)} = <Text strong>${dSubtotal.toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount Due">
                <Text strong style={{fontSize:15}}>${parseFloat(r?.total_amount ?? 0).toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={INV_COLOR[r?.status] ?? "default"}>{r?.status ?? "—"}</Tag>
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              message="Payment Instructions"
              description="Please contact MCHUSA billing at billing@mchusa.com to arrange payment"
            />
          </Modal>
        );
      })()}

    </Layout>
  );
}
